import type { KursDto, SumaKosztow, WierszKosztuEtapu, WierszKosztuModelu } from "@shared/kosztyTypy.js";
import { etykietaDostawcy, etykietaEtapu, formatujLiczbe, formatujTokeny, kosztDoPokazania } from "../../lib/koszty.js";
import { odmien } from "../../lib/polski.js";
import { Badge } from "../ui/Badge.js";
import { Tooltip } from "../ui/Tooltip.js";

/**
 * Tabele kosztow: per model i per etap, z wierszem sum. Wspolne dla karty "Koszt analizy" i strony "Koszty".
 *
 * Marker tabeli jak w MicHistory: role na kazdym elemencie (display: block na telefonie zabieralby tabeli semantyke)
 * i `data-etykieta` na komorkach, z ktorego mobilne mini-karty biora podpisy (TB-8, T-5).
 *
 * Kwota nigdy nie zastepuje braku danych: policzony koszt to kwota, czesciowy to "od <kwota>" z plakietka
 * "niepelny", a brak to kreska z powodem w dymku (KP-2). Powod jest tez tekstem dla czytnika, bo dymek to nie jedyny
 * nosnik (TT-1).
 *
 * Plakietka "niepelny" jest domyslnie ostrzezeniem (strona "Koszty"). Karta "Koszt analizy" stoi na stronie analizy,
 * a tam bursztyn to zloto oceny, czyli "najlepsze" (decyzja autora metodyki z 29.10.2026), wiec podaje wariant
 * informacji. Sens niesie slowo "niepelny" i przedrostek "od", kolor jest dodatkiem (ST-3).
 */

/** Wariant plakietki "niepelny": ostrzezenie na stronie "Koszty", informacja w karcie kosztu na stronie analizy. */
type WariantNiepelnego = "warning" | "info";

function KomorkaKosztu({
  usd,
  kompletny,
  powody,
  kurs,
  wariantNiepelnego = "warning",
}: {
  usd: number | null;
  kompletny: boolean;
  powody: readonly string[];
  kurs: KursDto | null;
  wariantNiepelnego?: WariantNiepelnego;
}): React.JSX.Element {
  const k = kosztDoPokazania(usd, kompletny, powody, kurs);

  if (k.usd === null) {
    return (
      <Tooltip tekst={k.powod ?? ""}>
        <span className="koszt koszt--brak" tabIndex={0}>
          -<span className="tylko-dla-czytnika">{` (brak kosztu: ${k.powod})`}</span>
        </span>
      </Tooltip>
    );
  }

  const zawartosc = (
    <span className="koszt" tabIndex={k.niepelny ? 0 : undefined}>
      <span className="koszt__usd">{k.usd}</span>
      {k.pln && <span className="koszt__pln">{k.pln}</span>}
      {k.niepelny && <Badge wariant={wariantNiepelnego}>niepełny</Badge>}
      {k.niepelny && k.powod && <span className="tylko-dla-czytnika">{` (koszt niepełny: ${k.powod})`}</span>}
    </span>
  );
  return k.niepelny && k.powod ? <Tooltip tekst={k.powod}>{zawartosc}</Tooltip> : zawartosc;
}

function PodpisModelu({ wiersz }: { wiersz: WierszKosztuModelu }): React.JSX.Element {
  const etapy = wiersz.etapy.map(etykietaEtapu).join(", ");
  return (
    <>
      <span className="tabela-danych__glowna">{wiersz.model}</span>
      <span className="tabela-danych__podpis">{`${etykietaDostawcy(wiersz.dostawca)} - ${etapy}`}</span>
    </>
  );
}

/** Wartosc komorki z podpisem pod spodem. Jedno dziecko komorki: na telefonie komorka to flex (podpis kolumny z lewej, tresc z prawej). */
function WartoscZPodpisem({ podpis, children }: { podpis?: string | false | null; children: React.ReactNode }): React.JSX.Element {
  return (
    <span className="tabela-danych__wartosc">
      <span>{children}</span>
      {podpis && <span className="tabela-danych__podpis">{podpis}</span>}
    </span>
  );
}

function OpisWywolan({ wywolania, proby, nieudane }: { wywolania: number; proby: number; nieudane: number }): React.JSX.Element {
  const podpis =
    (proby !== wywolania || nieudane > 0) &&
    `${formatujLiczbe(proby)} ${odmien(proby, "próba", "próby", "prób")}` +
      (nieudane > 0 ? `, w tym ${formatujLiczbe(nieudane)} ${odmien(nieudane, "nieudana", "nieudane", "nieudanych")}` : "");
  return <WartoscZPodpisem podpis={podpis}>{formatujLiczbe(wywolania)}</WartoscZPodpisem>;
}

/**
 * Uwagi z cennika (np. "bez rabatu za cache: szacunek moze byc zawyzony") widoczne pod tabela, nie tylko w danych: to
 * zastrzezenia do samej kwoty. Powtarzajace sie zdania sa wypisane raz, z lista modeli, ktorych dotycza.
 */
function UwagiDoWyceny({ wiersze }: { wiersze: readonly WierszKosztuModelu[] }): React.JSX.Element | null {
  const wgTresci = new Map<string, string[]>();
  for (const w of wiersze) {
    for (const uwaga of w.uwagi) {
      const modele = wgTresci.get(uwaga);
      if (modele) {
        if (!modele.includes(w.model)) modele.push(w.model);
      } else {
        wgTresci.set(uwaga, [w.model]);
      }
    }
  }
  if (wgTresci.size === 0) return null;
  return (
    <ul className="koszt-uwagi" aria-label="Uwagi do wyceny">
      {[...wgTresci.entries()].map(([uwaga, modele]) => (
        <li key={uwaga}>
          <span className="koszt-uwagi__model">{modele.join(", ")}</span>: {uwaga}
        </li>
      ))}
    </ul>
  );
}

export function TabelaModeli({
  wiersze,
  suma,
  kurs,
  opis,
  wariantNiepelnego,
}: {
  wiersze: readonly WierszKosztuModelu[];
  suma: SumaKosztow;
  kurs: KursDto | null;
  /** Nazwa tabeli dla czytnika ekranu. */
  opis: string;
  /** Wariant plakietki "niepelny" w wierszach i w sumie. Brak: ostrzezenie (strona "Koszty"). */
  wariantNiepelnego?: WariantNiepelnego;
}): React.JSX.Element {
  const powodySumy = [...new Set(wiersze.flatMap((w) => (w.kompletny ? [] : w.powody)))];
  return (
    <>
      <div className="tabela-danych-wrap">
        <table className="tabela-danych" role="table" aria-label={opis}>
          <thead role="rowgroup">
            <tr role="row">
              <th role="columnheader">Model</th>
              <th role="columnheader" className="is-num">
                Wywołania
              </th>
              <th role="columnheader" className="is-num">
                Tokeny wejścia / wyjścia
              </th>
              <th role="columnheader" className="is-num">
                Koszt
              </th>
            </tr>
          </thead>
          <tbody role="rowgroup">
            {wiersze.map((w) => (
              <tr role="row" key={`${w.dostawca}|${w.model}`}>
                <td role="cell" data-etykieta="Model">
                  <PodpisModelu wiersz={w} />
                </td>
                <td role="cell" className="is-num" data-etykieta="Wywołania">
                  <OpisWywolan wywolania={w.wywolania} proby={w.proby} nieudane={w.nieudane} />
                </td>
                <td role="cell" className="is-num" data-etykieta="Tokeny wejścia / wyjścia">
                  <WartoscZPodpisem podpis={w.sekundyAudio !== null && `${formatujLiczbe(w.sekundyAudio / 60, 1)} min audio`}>
                    {formatujTokeny(w.tokenyWejscia, w.tokenyWyjscia)}
                  </WartoscZPodpisem>
                </td>
                <td role="cell" className="is-num" data-etykieta="Koszt">
                  <KomorkaKosztu
                    usd={w.usd}
                    kompletny={w.kompletny}
                    powody={w.powody}
                    kurs={kurs}
                    wariantNiepelnego={wariantNiepelnego}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot role="rowgroup">
            <tr role="row" className="tabela-danych__suma">
              <th role="rowheader" scope="row">
                Razem
              </th>
              <td role="cell" className="is-num" data-etykieta="Wywołania">
                <OpisWywolan wywolania={suma.wywolania} proby={suma.proby} nieudane={suma.nieudane} />
              </td>
              <td role="cell" className="is-num" data-etykieta="Tokeny wejścia / wyjścia">
                <WartoscZPodpisem>{formatujTokeny(suma.tokenyWejscia, suma.tokenyWyjscia)}</WartoscZPodpisem>
              </td>
              <td role="cell" className="is-num" data-etykieta="Koszt">
                <KomorkaKosztu
                  usd={suma.usd}
                  kompletny={suma.kompletna}
                  powody={powodySumy}
                  kurs={kurs}
                  wariantNiepelnego={wariantNiepelnego}
                />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <UwagiDoWyceny wiersze={wiersze} />
    </>
  );
}

export function TabelaEtapow({
  wiersze,
  kurs,
  opis,
}: {
  wiersze: readonly WierszKosztuEtapu[];
  kurs: KursDto | null;
  opis: string;
}): React.JSX.Element {
  return (
    <div className="tabela-danych-wrap">
      <table className="tabela-danych" role="table" aria-label={opis}>
        <thead role="rowgroup">
          <tr role="row">
            <th role="columnheader">Etap</th>
            <th role="columnheader" className="is-num">
              Wywołania
            </th>
            <th role="columnheader" className="is-num">
              Koszt
            </th>
          </tr>
        </thead>
        <tbody role="rowgroup">
          {wiersze.map((e) => (
            <tr role="row" key={e.etap}>
              <td role="cell" data-etykieta="Etap">
                <span className="tabela-danych__glowna">{etykietaEtapu(e.etap)}</span>
              </td>
              <td role="cell" className="is-num" data-etykieta="Wywołania">
                <OpisWywolan wywolania={e.wywolania} proby={e.proby} nieudane={0} />
              </td>
              <td role="cell" className="is-num" data-etykieta="Koszt">
                <KomorkaKosztu
                  usd={e.usd}
                  kompletny={e.kompletny}
                  powody={
                    e.kompletny
                      ? []
                      : e.powody && e.powody.length > 0
                        ? e.powody
                        : ["część wywołań tego etapu nie ma policzonego kosztu albo odczyt nie objął całego okresu"]
                  }
                  kurs={kurs}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
